import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiPackage, FiChevronRight, FiCalendar } from 'react-icons/fi';
import { getMyOrders } from '../../services/api';
import './MyOrdersPage.css';

const STATUS_TABS = [
  { value: '', label: 'All Orders' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_CONFIG = {
  placed:      { label: 'Placed',      cls: 'badge-info' },
  confirmed:   { label: 'Confirmed',   cls: 'badge-primary' },
  processing:  { label: 'Processing',  cls: 'badge-warning' },
  shipped:     { label: 'Shipped',     cls: 'badge-accent' },
  delivered:   { label: 'Delivered',   cls: 'badge-success' },
  cancelled:   { label: 'Cancelled',   cls: 'badge-danger' },
};


const picsum = (seed) => `https://picsum.photos/seed/${seed}/48/48`;

export default function MyOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (activeTab) params.status = activeTab;
      const { data } = await getMyOrders(params);
      setOrders(data.orders);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      console.error('My orders error:', err?.response?.data || err.message);
      setOrders([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => { setPage(1); }, [activeTab]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleTab = (val) => setActiveTab(val);

  return (
    <div className="my-orders-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1><FiPackage size={24} /> My Orders</h1>
          <p className="text-muted">Track and manage your purchases</p>
        </div>

        {/* Tabs */}
        <div className="order-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              className={`order-tab ${activeTab === tab.value ? 'active' : ''}`}
              onClick={() => handleTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="loading-center"><div className="spinner" /><span>Loading orders…</span></div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h3>No orders found</h3>
            <p>{activeTab ? `No ${activeTab} orders yet.` : "You haven't placed any orders yet."}</p>
            <Link to="/products" className="btn btn-primary">Start Shopping</Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => {
              const { label, cls } = STATUS_CONFIG[order.orderStatus] || { label: order.orderStatus, cls: 'badge-info' };
              const date = new Date(order.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });

              return (
                <Link to={`/orders/${order._id}`} key={order._id} className="order-row card card-hover">
                  {/* Top row */}
                  <div className="order-row-header">
                    <div className="order-meta">
                      <span className="order-num">{order.orderNumber}</span>
                      <span className="order-date">
                        <FiCalendar size={12} /> {date}
                      </span>
                    </div>
                    <div className="order-row-right">
                      <span className={`badge ${cls}`}>{label}</span>
                      <FiChevronRight size={16} className="order-chevron" />
                    </div>
                  </div>

                  {/* Items thumbnails */}
                  <div className="order-items-preview">
                    <div className="order-thumbs">
                      {order.items.slice(0, 4).map((item, i) => (
                        <div key={item._id || i} className="order-thumb-wrap">
                          <img
                            src={item.image || picsum(item.name)}
                            alt={item.name}
                            className="order-thumb"
                            onError={(e) => { e.target.src = picsum(item.name || i); }}
                          />
                        </div>
                      ))}
                      {order.items.length > 4 && (
                        <div className="order-thumb-more">+{order.items.length - 4}</div>
                      )}
                    </div>
                    <div className="order-item-names">
                      {order.items.slice(0, 2).map((item, i) => (
                        <p key={i} className="order-item-name-text">{item.name}</p>
                      ))}
                      {order.items.length > 2 && (
                        <p className="text-muted text-sm">+{order.items.length - 2} more item{order.items.length - 2 > 1 ? 's' : ''}</p>
                      )}
                    </div>
                  </div>

                  {/* Footer row */}
                  <div className="order-row-footer">
                    <span className="text-muted text-sm">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
                    <span className="order-total">Rs. {Number(order.total).toFixed(2)}</span>
                  </div>
                </Link>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                <span className="page-info">Page {page} of {totalPages}</span>
                <button className="page-btn" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
